import {
  useErrorsServiceToken,
  useGameStatusServiceToken,
  useGlobalWindowToken,
  useLoggerServiceToken,
  useNavigateServiceToken,
  usePlatformServiceToken,
  useResourcesToken,
  useTrackingServiceToken,
} from '@/di/di.root-container.hooks';
import {AnalyticsNewInvestGameButtonType} from '@/services/TrackingService/TrackingService.constants';

import {FC, useEffect, useMemo} from 'react';

import {useMatchMedia} from '@specials/match-media';
import {HttpError} from '@specials/project-user';

import {useGameStatusStore} from '@/stores/connectors';
import {AppErrorFullScreen} from '@/ui/common/AppError/AppErrorFullScreen/AppErrorFullScreen';
import {UIErrorComponent} from '@/ui/common/AppError/components/constants';
import {ButtonLink, ButtonLinkMods, ButtonLinkSize} from '@/ui/kit/ButtonLink/ButtonLink';
import {Image} from '@/ui/kit/Image/Image';
import {ParameterizedText} from '@/ui/kit/ParameterizedText/ParameterizedText';
import {PreloaderCustom} from '@/ui/kit/PreloaderCustom/PreloaderCustom';

import s from './AppErrorGameIsNotAProgress.module.css';

export interface Props {
  error: HttpError | unknown;
}

interface ContentProps {
  title: string;
  subtitleMobile: string;
  subtitleDesktop: string;
  cta?: {
    text: string;
    href: CtaHrefProps;
  };
}

interface CtaHrefProps {
  mobileLink: string;
  mobileLinkIos: string;
  mbAppDeeplink: string;
  investAppDeeplink: string;
}

export const AppErrorGameIsNotAProgress: FC<Props> = ({error}) => {
  const {kotletkaImageBody, kotletkaImageFingers, showBeforeContent, beforeResults, afterResults} =
    useResourcesToken().content.AppErrorGameIsNotAProgress;

  const errorsService = useErrorsServiceToken();
  const loggerService = useLoggerServiceToken();
  const platformService = usePlatformServiceToken();
  const gameStatusService = useGameStatusServiceToken();
  const trackingService = useTrackingServiceToken();
  const navigationService = useNavigateServiceToken();
  const globalWindow = useGlobalWindowToken();

  const gameStatusStore = useGameStatusStore();

  const {isMobile} = useMatchMedia();

  const isStatusIdle = gameStatusStore.isIdleFetchStatus();
  const isSuccessFetchStatus = gameStatusStore.isSuccessFetchStatus();
  const isErrorFetchStatus = gameStatusStore.isErrorFetchStatus();

  const errorData = useMemo(() => errorsService.buildErrorData(error), [error]);

  const getCtaHref = (cta: CtaHrefProps): string | null => {
    if (platformService.isInvesting()) {
      return cta.investAppDeeplink;
    }

    if (platformService.isMb()) {
      return cta.mbAppDeeplink;
    }

    if (platformService.detectIsIos()) {
      return cta.mobileLinkIos;
    }

    if (isMobile) {
      return cta.mobileLink;
    }

    return null;
  };

  useEffect(() => {
    if (isStatusIdle) {
      void gameStatusService.fetchGameStatus();
    }

    if (typeof error !== 'undefined' && error !== null) {
      loggerService.logDisplayErrorUIComponent(error, errorData.breadcrumbs, UIErrorComponent.GameIsNotAProgress);
    }
  }, []);

  if (isSuccessFetchStatus) {
    const isGameFinishedBeforeResults = gameStatusStore.isStatusGameFinishedBeforeResults();
    const {title, subtitleMobile, subtitleDesktop, cta}: ContentProps =
      isGameFinishedBeforeResults && showBeforeContent ? beforeResults : afterResults;

    const subtitle = isMobile ? subtitleMobile : subtitleDesktop;

    const renderCta = (): JSX.Element | null => {
      if (cta) {
        const ctaHref = getCtaHref(cta.href);

        if (ctaHref) {
          return (
            <ButtonLink
              href={ctaHref}
              onClick={() => {
                trackingService.trackClickToNewInvestGameButton(AnalyticsNewInvestGameButtonType.EndGame);
                globalWindow.setTimeout(() => {
                  navigationService.openLink('tcsgames://action/close');
                }, 1000);
                // navigationService.openLink(ctaHref);
              }}
              mod={ButtonLinkMods.ButtonPrimary}
              size={ButtonLinkSize.S}
              className={s.button}
            >
              {cta.text}
            </ButtonLink>
          );
        }
      }
      return null;
    };

    return (
      <div className={s.root}>
        <div className={s.wrapper}>
          <Image src={kotletkaImageBody} alt={title} className={s.kotletkaImageBody} />
          <Image src={kotletkaImageFingers} alt={title} className={s.kotletkaImageFingers} />

          <h3 className={s.title}>{title}</h3>
          <div className={s.text}>
            <ParameterizedText template={subtitle} />
          </div>
          {renderCta()}
        </div>
      </div>
    );
  }

  if (isErrorFetchStatus) {
    return <AppErrorFullScreen error={gameStatusStore.getErrorFetchData()} />;
  }

  return (
    <div className={s.root}>
      <div className={s.preloaderWrapper}>
        <PreloaderCustom />
      </div>
    </div>
  );
};
